import { useCallback, useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
    DEFAULT_STORY_BACKGROUND,
    STORY_BACKGROUNDS,
    type StoryBackgroundKey
} from 'components/UI/BackgroundPicker/BackgroundPicker'
import DateModal from 'components/UI/Modal/Date/DateModal'
import { useModalContext } from 'context/modalContext'
import { type Orientation } from 'helpers/media'
import { useStoryNavigate, useStoryUploadProgress } from 'services/stories'
import { type NewStory } from 'types/api/story'
import { type IPoll } from 'types/interfaces/ITypes'
import {
    formatStory,
    formatStoryFromSharedMedia,
    initializeDateFromQuery
} from 'utils/stories'

export type StoryVideos = Array<{
  id?: string
  path?: string
  s3_path?: string
  preview_path?: string
  orientation?: Orientation
  video_exif?: any
  isLocal?: boolean
  status?: string
  duration?: number
}>
const normalizeBackgroundKey = (
    background?: string | null
): StoryBackgroundKey => {
    if (background && background in STORY_BACKGROUNDS) {
        return background as StoryBackgroundKey
    }

    return DEFAULT_STORY_BACKGROUND
}

export function useCreateStory() {
    const modalData = useModalContext()
    const { t } = useTranslation()

    const [pollData, setPollData] = useState<IPoll | null>(null)
    const [addPollOpen, setAddPollOpen] = useState(false)

    const [isAddTextOpen, setIsAddTextOpen] = useState(false)
    const [textValue, setTextValue] = useState('')
    const [textPosition, setTextPosition] = useState<[number, number]>([50.0, 50.0])
    const [initialPosition, setInitialPos] = useState([50.0, 50.0])
    const [initialText, setInitialText] = useState('')
    const [isTextDragged, setIsTextDragged] = useState(false)

    const [scheduleDate, setScheduleDate] = useState<Date | null>(null)
    const [mediaType, setMediaType] = useState<any>()

    // IMPORTANT: store the NAME, not the CSS gradient
    const [background, setBackground] = useState<StoryBackgroundKey>(
        DEFAULT_STORY_BACKGROUND
    )
    const [image, setImage] = useState<{
    id?: string
    path: string
    orientation: Orientation
    exif_data?: any
    }>()

    const [video, setVideo] = useState<StoryVideos>()

    useEffect(() => {
        if (localStorage.getItem('share-story')) {
            const newStoryMedia = localStorage.getItem('share-story')
            const parsedMedia = newStoryMedia ? JSON.parse(newStoryMedia) : null
            const storyFromSharedMedia = formatStoryFromSharedMedia(parsedMedia)
            initializeStory(storyFromSharedMedia)
            localStorage.removeItem('share-story')
        }
    }, [])

    useEffect(() => {
        initializeDateFromQuery(setScheduleDate)
    }, [])

    const initializeStory = (newStory: NewStory) => {
        if (newStory) {
            if (newStory.background_color) {
                setBackground(normalizeBackgroundKey(newStory.background_color))
            }

            if (newStory.photo) {
                setImage(newStory.photo)
            }

            if (newStory.videos) {
                setVideo([
                    {
                        orientation: newStory.videos[0].orientation,
                        s3_path: newStory.videos[0].path,
                        video_exif: newStory.videos[0].video_exif
                    }
                ])
            }

            if (newStory.text) {
                setTextPosition([newStory.text.x, newStory.text.y])
                setInitialPos([newStory.text.x, newStory.text.y])
                setTextValue(newStory.text.value)
                setInitialText(newStory.text.value)
                setIsAddTextOpen(true)
            }

            if (newStory.poll) {
                setPollData({
                    ...newStory.poll,
                    pollBg: '',
                    pollBgImg: null
                })
            }

            if (newStory.schedule_date) {
                setScheduleDate(new Date(newStory.schedule_date))
            }
        }
    }

    const handleRemoveText = useCallback(() => {
        setIsAddTextOpen(false)
        setTextPosition([50, 50])
        setTextValue('')
    }, [])

    const handleSetCoordinates = useCallback(
        (x: number, y: number) => {
            if (x !== textPosition[0] || y !== textPosition[1]) {
                setTextPosition([x, y])
            }
        },
        [textPosition]
    )

    const handleSetImageData = useCallback(
    (
        id: string | undefined,
        path: string,
        orientation: Orientation,
        exif_data: any
    ) => {
        setImage({
        id,
        path,
        orientation,
        exif_data
        })
    },
    []
    )

    const handleSetVideoData = useCallback((videos: StoryVideos) => {
        setVideo(videos)
    }, [])

    function clearMedia() {
        const previewPath = video?.[0]?.preview_path

        if (previewPath?.startsWith('blob:')) {
            try {
                URL.revokeObjectURL(previewPath)
            } catch {}
        }

        setImage(undefined)
        setVideo(undefined)
    }

    const { handleCloseClick, handleSettingsClick } = useStoryNavigate()
    const { handleSetProgressInfo, uploadProgress, setUploadProgress } =
        useStoryUploadProgress()

    const toggleDateModal = () => {
        modalData.addModal(
            t('schedulePost'),
            <DateModal
                confirmFn={setScheduleDate}
                newDateVal={scheduleDate ?? new Date()}
            />
        )
    }

    function handleSetPollFunction(data: IPoll) {
        setPollData({
            ...data,
            pollBg: 'transparent'
        })
    }

    function handleClearPollData() {
        setPollData(null)
    }

    function handleOpenText() {
        setIsAddTextOpen(true)
    }

    function handleOpenPoll() {
        setAddPollOpen(true)
    }

    function handleRemoveSchedule() {
        setScheduleDate(null)
    }

    function handleUploadAbort() {
        uploadProgress?.cancelCb?.()
        setUploadProgress(null)
    }

    const formatedStory = useMemo(
        () =>
            formatStory({
                background, // this is now "modernGradient", "pastelAbstract", etc.
                image,
                video,
                textPosition,
                textValue,
                pollData,
                scheduleDate,
                scheduleTimezone: scheduleDate
                    ? Intl.DateTimeFormat().resolvedOptions().timeZone
                    : null
            }),
        [background, image, video, textPosition, textValue, pollData, scheduleDate]
    )

    return {
        background,
        image,
        setScheduleDate,
        addPollOpen,
        clearMedia,
        setAddPollOpen,
        setPollData,
        video,
        isAddTextOpen,
        initialPosition,
        initialText,
        handleSetCoordinates,
        pollData,
        handleRemoveText,
        setBackground,
        isTextDragged,
        textValue,
        setTextValue,
        setIsTextDragged,
        setIsAddTextOpen,
        scheduleDate,
        handleSetImageData,
        handleSetVideoData,
        formatedStory,
        handleUploadAbort,
        handleSettingsClick,
        handleCloseClick,
        handleSetProgressInfo,
        toggleDateModal,
        uploadProgress,
        handleSetPollFunction,
        handleRemoveSchedule,
        handleClearPollData,
        handleOpenPoll,
        handleOpenText,
        mediaType,
        setMediaType
    }
}